import { ArrowRightOutlined } from '@ant-design/icons';
import { Col, Flex, Row, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ChevronRightIcon } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SingleShipmentDataExtended } from '@/apis/types';
import { BellIcon } from '@/assets/icons/icons';
import { isCryoportCompany } from '@/common/constants/cryoport';
import { isWorldCourierCompany } from '@/common/constants/worldCourier';
import { useToken } from '@/common/helper';
import { useManualShipmentCreationFlags } from '@/hooks/useManualShipmentCreationFlags';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { CommonDrawer, CustomPagination, EllipsisTooltip } from '@/components';
import BookShipmentDrawer from '@/pages/Shipments/BookShipmentDrawer';
import QuoteShipmentDrawer from '@/pages/Shipments/QuoteShipmentDrawer';
import ShipmentDrawer from '@/pages/Shipments/ShipmentDrawer';
import { getGxPStatusBadgeConfig } from '../utils';
import GxPRecipientCell from './GxPRecipientCell/GxPRecipientCell';
import { GxPReportRecord, GxPReportTableProps } from './types';

const EMPTY_VALUE = '';

const HeaderText = ({ text }: { text: string }) => (
  <Typography.Text
    className='gxp-table-header-text'
    ellipsis={{ tooltip: text }}
  >
    {text}
  </Typography.Text>
);

const EllipsisText = ({
  text,
  className,
  underline,
  type,
}: {
  text?: string | null;
  className?: string;
  underline?: boolean;
  type?: 'secondary';
}) => {
  const displayText = text || EMPTY_VALUE;

  return (
    <Typography.Text
      className={className}
      ellipsis={displayText ? { tooltip: displayText } : true}
      type={type}
      underline={underline}
    >
      {displayText}
    </Typography.Text>
  );
};

const GxPReportTable: React.FC<GxPReportTableProps> = ({
  GxPTableList,
  page,
  setPage,
  totalPages,
  activeTab,
}) => {
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { formatDateTime } = useUserDateTimeFormat();

  const { quote, book } = useManualShipmentCreationFlags();
  const worldCourierEnabled = useMemo(
    () => isWorldCourierCompany(decodedToken?.enablecustomerleg),
    [decodedToken?.enablecustomerleg]
  );
  const cryoportEnabled = useMemo(
    () => isCryoportCompany(decodedToken?.company_id),
    [decodedToken?.company_id]
  );

  const shipmentDrawerWrapRef = useRef<HTMLDivElement>(null);
  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    name: '',
    shipmentId: null as string | null,
    alert: null as unknown,
  });
  const [isScrollMode, setIsScrollMode] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 1280 : false
  );
  const [openBookShipmentDrawer, setOpenBookShipmentDrawer] = useState(false);
  const [openQuoteShipmentDrawer, setOpenQuoteShipmentDrawer] = useState(false);
  const [quotePrefillShipment, setQuotePrefillShipment] = useState<
    SingleShipmentDataExtended | undefined
  >();
  const [bookPrefillShipment, setBookPrefillShipment] = useState<
    SingleShipmentDataExtended | undefined
  >();
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;
      setIsScrollMode(width < 1280);
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openShipment = useCallback((shipmentId: string) => {
    setDrawerState({
      isOpen: true,
      name: 'Detail',
      shipmentId,
      alert: null,
    });
  }, []);

  const handleQuoteFromShipment = useCallback(
    (shipment: SingleShipmentDataExtended) => {
      setQuotePrefillShipment(shipment);
      setOpenQuoteShipmentDrawer(true);
    },
    []
  );

  const handleBookFromShipment = useCallback(
    (shipment: SingleShipmentDataExtended) => {
      setBookPrefillShipment(shipment);
      setOpenBookShipmentDrawer(true);
    },
    []
  );

  const badge = (label: string) => {
    const config = getGxPStatusBadgeConfig(label);
    return (
      <Tag
        className='GxP-listing-tag'
        style={{ backgroundColor: config.bgColor, color: config.textColor }}
      >
        <EllipsisTooltip text={label} className='GxP-listing-tag__text' />
      </Tag>
    );
  };

  const hasLocationRoute = (record: GxPReportRecord) =>
    Boolean(
      record.from_location || record.to_location || record.shipment_route_label
    );

  const columns: ColumnsType<GxPReportRecord> = [
    {
      title: <HeaderText text='Shipment' />,
      dataIndex: 'shipment_reference',
      width: isScrollMode ? 260 : '18%',
      render: (_, record) => (
        <Flex vertical gap={2}>
          <EllipsisText
            className='gxp-shipment-reference'
            text={record.shipment_reference}
            underline
          />

          {hasLocationRoute(record) && (
            <Flex align='center' gap={4} className='gxp-location-route'>
              <EllipsisText
                type='secondary'
                text={record.from_location || record.shipment_route_label}
              />
              {record.from_location && record.to_location && (
                <ArrowRightOutlined className='gxp-location-arrow' />
              )}
              <EllipsisText type='secondary' text={record.to_location} />
            </Flex>
          )}
        </Flex>
      ),
    },
    {
      title: <HeaderText text='Status' />,
      dataIndex: 'status_label',
      width: isScrollMode ? 115 : '14%',
      className: 'gxp-status-column',
      render: badge,
    },
    {
      title: <HeaderText text='Reference' />,
      dataIndex: 'references_label',
      width: isScrollMode ? 175 : '14%',
      className: 'gxp-reference-column',
      render: (_, record) => (
        <Flex vertical gap={2}>
          {record.references_label && (
            <EllipsisText
              className='gxp-reference-text'
              text={record.references_label}
            />
          )}
          {record.references_label_2 && (
            <EllipsisText
              className='gxp-reference-text'
              type='secondary'
              text={record.references_label_2}
            />
          )}
        </Flex>
      ),
    },
    {
      title: <HeaderText text='Delivered' />,
      dataIndex: 'delivered_at',
      width: isScrollMode ? 150 : '13%',
      render: (value: string | null) => {
        return <EllipsisText text={value || '--'} />;
      },
    },
    {
      title: <HeaderText text='Excursions' />,
      dataIndex: 'excursions_label',
      width: isScrollMode ? 110 : '9%',
      render: badge,
    },
    {
      title: <HeaderText text='Data' />,
      dataIndex: 'data_label',
      width: isScrollMode ? 100 : '7%',
      render: badge,
    },
    // Sent tab swaps Disposition for Recipients
    activeTab === 'sent'
      ? {
          title: <HeaderText text='Recipients' />,
          key: 'sent_recipients',
          width: isScrollMode ? 170 : '15%',
          render: (_, record) => <GxPRecipientCell record={record} />,
        }
      : {
          title: <HeaderText text='Disposition' />,
          key: 'disposition',
          width: isScrollMode ? 170 : '15%',
          render: (_, record) => {
            const dispositionText = record.can_review
              ? 'Review'
              : record.published_by_name
                ? `Published ${record.published_by_name}`
                : record.status_label;

            return (
              <Flex vertical>
                <EllipsisText text={dispositionText} />
                {record.published_at && (
                  <EllipsisText
                    type='secondary'
                    text={formatDateTime(record.published_at, {
                      includeYear: false,
                      includeTime: false,
                    })}
                  />
                )}
              </Flex>
            );
          },
        },
    {
      title: '',
      dataIndex: 'is_notification',
      width: isScrollMode ? 40 : '5%',
      align: 'center',
      render: (value: boolean) =>
        value ? <BellIcon width={12} height={14} color='#065DFF' /> : null,
    },
    {
      title: '',
      key: 'action',
      width: isScrollMode ? 45 : '5%',
      render: () => <ChevronRightIcon width={16} height={16} />,
    },
  ];

  return (
    <>
      <div className='settings-content-wrapper'>
        <Table
          rowKey='report_id'
          columns={columns}
          dataSource={GxPTableList}
          pagination={false}
          className='settings-table gxp-report-table'
          tableLayout='fixed'
          size='middle'
          scroll={isScrollMode ? { x: 'max-content' } : undefined}
          onRow={(record) => ({
            onClick: () => openShipment(record.shipment_id),
          })}
        />

        {GxPTableList.length > 0 && (
          <Row className='w-full mt-2' justify='center'>
            <Col>
              <CustomPagination
                page={page}
                totalPages={totalPages}
                onPageChange={(nextPage) => setPage(Math.max(1, nextPage))}
              />
            </Col>
          </Row>
        )}
      </div>

      <div ref={shipmentDrawerWrapRef}>
        <ShipmentDrawer
          fromTable
          onLinkedShipmentClick={() => {}}
          allPermissions={null}
          drawerState={drawerState}
          toggleDrawer={(state, name, shipmentId, alert) =>
            setDrawerState({
              isOpen: state === 'open',
              name,
              shipmentId: state === 'open' ? shipmentId : null,
              alert: alert ?? null,
            })
          }
          shipmentSuccess={() => {}}
          filterByLinkCode={() => {}}
          onEditShipmentSuccess={() => {}}
          onQuoteFromShipment={quote ? handleQuoteFromShipment : undefined}
          onBookFromShipment={
            !book || !(worldCourierEnabled || cryoportEnabled)
              ? undefined
              : handleBookFromShipment
          }
          detailRefreshKey={detailRefreshKey}
        />
      </div>

      {openBookShipmentDrawer && (
        <CommonDrawer
          open={openBookShipmentDrawer}
          onClose={() => {
            setOpenBookShipmentDrawer(false);
            setBookPrefillShipment(undefined);
          }}
          width={520}
        >
          <BookShipmentDrawer
            key={bookPrefillShipment?.id ?? 'menu-book'}
            onClose={() => {
              setOpenBookShipmentDrawer(false);
              setBookPrefillShipment(undefined);
            }}
            shellShipment={bookPrefillShipment}
            onPromoteSuccess={() => setDetailRefreshKey((k) => k + 1)}
          />
        </CommonDrawer>
      )}

      {openQuoteShipmentDrawer && (
        <CommonDrawer
          open={openQuoteShipmentDrawer}
          onClose={() => {
            setOpenQuoteShipmentDrawer(false);
            setQuotePrefillShipment(undefined);
          }}
          width={520}
        >
          <QuoteShipmentDrawer
            onClose={() => {
              setOpenQuoteShipmentDrawer(false);
              setQuotePrefillShipment(undefined);
            }}
            shellShipment={quotePrefillShipment}
            onPromoteSuccess={() => setDetailRefreshKey((k) => k + 1)}
          />
        </CommonDrawer>
      )}
    </>
  );
};

export default GxPReportTable;
